import { Icon, Alert, Button, TextLink } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ActivateAccountMessage } from '../../../components/ui/ActivateAccountMessage';
import { Container } from '../../../components/ui/Container';
import { PageLayout } from '../../../components/ui/PageLayout';
import { WelcomeMGMRewards } from '../../../components/ui/WelcomeMGMRewards';
import { useAppRedirect } from '../../../hooks/useAppRedirect';
import { useContent } from '../../../hooks/useContent';
import { useRemove2faSkipForNow } from '../../../hooks/useRemove2faSkipForNow';
import { routes } from '../../../routes';
import { useEvent } from '../../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../../utils/auth/AuthProvider';
import { TWO_FACTOR_ENROLLMENT_PROMPTED_KEY } from '../../../utils/constants';
import { IDENTITY_WIDGET_PATHS, IDW_FLOW_PARAM } from '../../../utils/constants';
import { getFeaturesByClientId } from '../../../utils/getFeaturesByClientId';
import { getPersistedParameters } from '../../../utils/persistParameterContext';
import { ConfirmModal } from '../ConfirmModal';

import {
  StyledVerificationText,
  StyledDescriptionText,
  StyledEmailFieldLabel,
  StyledEnrollmentSection,
  StyledEnrollmentButtonGroup,
  StyledFooterLinks,
  StyledSkipButton,
  StyledDivContainer,
} from './styles';
import { MfaEnrollmentFlowConfig, MfaEnrollmentFlowType, MFA_ENROLLMENT_FLOW_CONFIG } from './types';

export function MFAEnrollmentPage({ enrollmentFlow }: { enrollmentFlow?: MfaEnrollmentFlowType }) {
  const { authClient } = useAuthContext();
  const { isAllowOktaSignInWithRedirect } = getFeaturesByClientId();
  const redirectToApp = useAppRedirect();
  const params = getPersistedParameters();
  const { getContentByPath } = useContent();
  const trackEvent = useEvent();
  const { remove2faSkipForNow } = useRemove2faSkipForNow();
  const [openAlert, setOpenAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showWelcomeMgmRewardsScreen, setShowWelcomeMgmRewardsScreen] = useState(false);
  const [showAccountActivatedScreen, setShowAccountActivatedScreen] = useState(false);
  // Get flow from persisted parameters
  const idwFlow = params?.[IDW_FLOW_PARAM] as MfaEnrollmentFlowType | undefined;
  const currentFlow = enrollmentFlow || idwFlow;

  const handleRedirect = useCallback(async () => {
    if (isAllowOktaSignInWithRedirect) {
      await authClient.handleOktaSignInWithRedirect();
    } else {
      redirectToApp();
    }
  }, [isAllowOktaSignInWithRedirect, authClient, redirectToApp]);

  // Generic modal state (can be used for success/error messages)
  const [showPasskeyErrorModal, setShowPasskeyErrorModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalCancelLabel, setModalCancelLabel] = useState<string>('');

  // Get verification config based on query parameter
  const verificationConfig = useMemo((): MfaEnrollmentFlowConfig => {
    if (currentFlow && currentFlow in MFA_ENROLLMENT_FLOW_CONFIG) {
      return MFA_ENROLLMENT_FLOW_CONFIG[currentFlow];
    }
    // Default fallback to current behavior
    return {
      message: 'Your email is verified',
      iconName: 'checkmark-circle',
      iconColor: '#007636',
    };
  }, [currentFlow]);

  // Dynamic page name based on verification type
  const pageName = useMemo(() => {
    if (currentFlow === 'activate') {
      return 'active account mfa setup';
    } else if (currentFlow === 'passkey') {
      return 'passkey added 2fa setup';
    } else if (currentFlow === 'signin') {
      return 'sign in mfa setup';
    }
    return 'sign up mfa setup';
  }, [currentFlow]);

  const hideSkipForNowCTA = useMemo(
    () => (currentFlow === 'signup' || currentFlow === 'activate' ? remove2faSkipForNow : false),
    [currentFlow, remove2faSkipForNow],
  );

  // Use dynamic page load event
  usePageLoadEvent('identity', pageName);

  const twoFactorEnrollmentPrompted = useRef(sessionStorage.getItem(TWO_FACTOR_ENROLLMENT_PROMPTED_KEY));
  const hasPersistedSession = useRef(false);
  useEffect(() => {
    if (currentFlow === 'signup' || currentFlow === 'activate') {
      if (twoFactorEnrollmentPrompted.current && remove2faSkipForNow) {
        handleRedirect();
        return;
      }

      if (!hasPersistedSession.current) {
        try {
          sessionStorage.setItem(TWO_FACTOR_ENROLLMENT_PROMPTED_KEY, 'true');
          hasPersistedSession.current = true;
        } catch (e) {
          console.warn(`Failed to persist ${TWO_FACTOR_ENROLLMENT_PROMPTED_KEY} in sessionStorage:`, e);
        }
      }
    }
  }, [currentFlow, handleRedirect, remove2faSkipForNow]);

  const handleEnrollWithEmail = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Enroll with email', 'unifiedmfactas', {}, true);

    const targetPath = IDENTITY_WIDGET_PATHS.MfaEnrollEmail;
    if (routes[targetPath]) {
      navigate(targetPath, true, { [IDW_FLOW_PARAM]: idwFlow });
    } else {
      setErrorMessage(getContentByPath('MFA.enrollment.systemError'));
      setOpenAlert(true);
    }
  }, [trackEvent, getContentByPath, idwFlow]);

  const handleEnrollWithSMS = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Enroll with text message (SMS)', 'unifiedmfactas', {}, true);
    const targetPath = IDENTITY_WIDGET_PATHS.MfaEnrollSms;
    if (routes[targetPath]) {
      navigate(targetPath, true, { [IDW_FLOW_PARAM]: idwFlow });
    } else {
      setErrorMessage(getContentByPath('MFA.enrollment.systemError'));
      setOpenAlert(true);
    }
  }, [trackEvent, getContentByPath, idwFlow]);

  const handleViewFAQs = useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      await trackEvent('identity – unified MFA CTAs', 'View FAQs', 'unifiedmfactas', {}, true);
      window.open(getContentByPath('MFA.enrollment.faqUrl'), '_blank');
    },
    [getContentByPath, trackEvent],
  );

  const handlePrivacyPolicy = useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      await trackEvent('identity – unified MFA CTAs', 'Privacy policy', 'unifiedmfactas', {}, true);
      window.open(getContentByPath('MFA.enrollment.privacyPolicyUrl'), '_blank');
    },
    [getContentByPath, trackEvent],
  );

  const handleSkipForNow = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Skip for now', 'unifiedmfactas', {}, true);

    if (currentFlow === 'passkey') {
      // No modal - directly show success screen based on original flow
      if (idwFlow === 'signup') {
        setShowWelcomeMgmRewardsScreen(true);
      } else if (idwFlow === 'activate') {
        setShowAccountActivatedScreen(true);
      }
      // Redirect after 2-second delay
      setTimeout(() => {
        handleRedirect();
      }, 2000);
    } else if (currentFlow === 'signup' || currentFlow === 'activate') {
      // Show confirmation modal - user must confirm they want to skip
      setModalCancelLabel(getContentByPath('MFA.confirmModal.addMethod'));
      setModalTitle(getContentByPath('MFA.confirmModal.skipEnrollmentTile'));
      setModalMessage(getContentByPath('MFA.confirmModal.skipEnrollmentDescription'));
      setShowPasskeyErrorModal(true);
      return;
    } else {
      handleRedirect();
    }
  }, [trackEvent, currentFlow, idwFlow, handleRedirect, getContentByPath]);

  const onCloseAlert = () => {
    setOpenAlert(false);
    setErrorMessage('');
  };
  // Generic confirm modal close handler
  const handleModalClose = useCallback(async () => {
    // Track only when user clicks "Add Method" button in the "Skip two-factor authentication?" modal
    if (modalCancelLabel === getContentByPath('MFA.confirmModal.addMethod')) {
      await trackEvent(
        'identity – unified MFA CTAs',
        'Add Method - Skip two-factor authentication?',
        'unifiedmfactas',
        {},
        true,
      );
    }

    setShowPasskeyErrorModal(false);
    setModalTitle('');
    setModalMessage('');
  }, [trackEvent, modalCancelLabel, getContentByPath]);

  // Generic confirm modal skip for now handler
  const handleSkipEnrollment = useCallback(async () => {
    // Track when user clicks "Skip for now" button in the "Skip two-factor authentication?" modal
    await trackEvent(
      'identity – unified MFA CTAs',
      'Skip for now - Skip two-factor authentication?',
      'unifiedmfactas',
      {},
      true,
    );
    if (currentFlow === 'activate') {
      setShowAccountActivatedScreen(true);
    } else {
      setShowWelcomeMgmRewardsScreen(true);
    }
    setShowPasskeyErrorModal(false);
    setModalTitle('');
    setModalMessage('');
    // Add 2-second delay before redirecting to app
    setTimeout(() => {
      handleRedirect();
    }, 2000);
  }, [handleRedirect, trackEvent, currentFlow]);

  // if (!mfaEnabled) {
  //   // TODO: Redirect to sign-in page
  //   return null;
  // }

  return (
    <PageLayout
      title={
        currentFlow == 'activate'
          ? getContentByPath('MFA.activateFlowTitle')
          : currentFlow == 'passkey'
          ? getContentByPath('MFA.passkeyFlowTitle')
          : getContentByPath('MFA.signupFlowTitle')
      }
      testId={`route-/mfa/enroll${enrollmentFlow ? `/${enrollmentFlow}` : ''}`}
      description='Set up multi-factor authentication for enhanced security'
    >
      {showAccountActivatedScreen ? (
        <ActivateAccountMessage />
      ) : showWelcomeMgmRewardsScreen ? (
        <WelcomeMGMRewards />
      ) : (
        <Container height='auto' width='491px'>
          <Icon
            name={verificationConfig.iconName}
            variant='outlined'
            size='large'
            color={verificationConfig.iconColor}
          />

          <StyledVerificationText variant='heading-medium-3' as='h1'>
            {verificationConfig.message}
          </StyledVerificationText>

          <StyledDescriptionText variant='body-regular-s'>
            {currentFlow === 'signin'
              ? getContentByPath('MFA.enrollment.signInFlowdescription')
              : getContentByPath('MFA.enrollment.description')}
          </StyledDescriptionText>
          {errorMessage && (
            <Alert
              open={openAlert}
              onClose={onCloseAlert}
              variant='error'
              message={errorMessage}
              data-testid='code-resent-error'
              role='alert'
            />
          )}
          <StyledDivContainer>
            <StyledEnrollmentSection>
              <StyledEmailFieldLabel variant='body-regular-m'>
                {getContentByPath('MFA.enrollment.twoFactor')}
              </StyledEmailFieldLabel>

              <StyledEnrollmentButtonGroup>
                {!verificationConfig.hideEmailEnrollment && (
                  <Button
                    variant='primary'
                    size='large'
                    onClick={handleEnrollWithEmail}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleEnrollWithEmail();
                      }
                    }}
                    testId='enroll-email'
                    type='button'
                    label={getContentByPath('MFA.enrollment.enrollWithEmail')}
                    shape='square'
                    fullWidth
                  />
                )}

                {!verificationConfig.hideSMSEnrollment && (
                  <Button
                    variant='secondary'
                    size='large'
                    onClick={handleEnrollWithSMS}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleEnrollWithSMS();
                      }
                    }}
                    testId='enroll-sms'
                    type='button'
                    label={getContentByPath('MFA.enrollment.enrollWithSMS')}
                    shape='square'
                    fullWidth
                  />
                )}
              </StyledEnrollmentButtonGroup>
            </StyledEnrollmentSection>
          </StyledDivContainer>
          <StyledFooterLinks>
            <TextLink onClick={handleViewFAQs} data-testid='view-faqs' variant='small'>
              {getContentByPath('MFA.enrollment.faq')}

              <Icon
                name='arrow-right'
                variant='outlined'
                size='small'
                color='#00012B'
                width={14}
                height={14}
                style={{ marginLeft: '4px' }}
              />
            </TextLink>

            <TextLink onClick={handlePrivacyPolicy} data-testid='privacy-policy' variant='small'>
              {getContentByPath('MFA.enrollment.privacyPolicy')}
            </TextLink>
          </StyledFooterLinks>
          {!hideSkipForNowCTA && (
            <>
              <StyledSkipButton
                variant='tertiary'
                size='large'
                onClick={handleSkipForNow}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSkipForNow();
                  }
                }}
                data-testid='skip-for-now'
                type='button'
              >
                {getContentByPath('MFA.enrollment.skipForNow')}
              </StyledSkipButton>

              <ConfirmModal
                isOpen={showPasskeyErrorModal}
                title={modalTitle}
                message={modalMessage}
                cancelLabel={modalCancelLabel}
                confirmLabel={getContentByPath('MFA.enrollment.skipForNow')}
                onClose={handleModalClose}
                onConfirm={handleSkipEnrollment}
                showCloseButton={true}
                testId='passkey-modal'
              />
            </>
          )}
        </Container>
      )}
    </PageLayout>
  );
}
